import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ColumnOrderState, ColumnSizingState } from '@tanstack/react-table';
import useUserPreference from '@/hooks/useUserPreference';

export interface ColumnLayout {
  sizes: ColumnSizingState;
  order: ColumnOrderState;
}

const EMPTY: ColumnLayout = { sizes: {}, order: [] };

const parse = (raw: string | null): ColumnLayout => {
  if (!raw) return EMPTY;
  try {
    const parsed = JSON.parse(raw);
    return {
      sizes: parsed?.sizes && typeof parsed.sizes === 'object' ? parsed.sizes : {},
      order: Array.isArray(parsed?.order) ? parsed.order : [],
    };
  } catch {
    return EMPTY;
  }
};

/**
 * The widths and the order the user gave a board's columns, kept on their
 * account so the arrangement they built follows them to another machine.
 *
 * A stored order is reconciled against the columns the board actually has right
 * now rather than trusted: a release that adds or removes a column would
 * otherwise leave people with a saved arrangement that silently drops it.
 */
const useColumnLayout = (layoutKey: string | undefined, columnIds: string[]) => {
  const [stored, setStored] = useUserPreference(layoutKey ? `table.${layoutKey}.layout` : '');
  const [layout, setLayout] = useState<ColumnLayout>(EMPTY);
  const isUserEdited = useRef(false);

  useEffect(() => {
    if (isUserEdited.current) return;
    setLayout(parse(stored));
  }, [stored]);

  const order = useMemo(() => {
    const known = new Set(columnIds);
    const kept = layout.order.filter((id) => known.has(id));
    // Columns the saved order never heard of go where the board puts them.
    const missing = columnIds.filter((id) => !kept.includes(id));
    if (missing.length === 0) return kept;
    const merged = [...kept];
    missing.forEach((id) => merged.splice(columnIds.indexOf(id), 0, id));
    return merged;
  }, [layout.order, columnIds]);

  const save = useCallback(
    (next: ColumnLayout) => {
      isUserEdited.current = true;
      setLayout(next);
      setStored(JSON.stringify(next));
    },
    [setStored]
  );

  const setSizes = useCallback(
    (sizes: ColumnSizingState) => save({ sizes, order: layout.order.length ? layout.order : columnIds }),
    [save, layout.order, columnIds]
  );

  const setOrder = useCallback((nextOrder: ColumnOrderState) => save({ sizes: layout.sizes, order: nextOrder }), [save, layout.sizes]);

  const reset = useCallback(() => save(EMPTY), [save]);

  const isCustomised = Object.keys(layout.sizes).length > 0 || layout.order.length > 0;

  return { sizes: layout.sizes, order, setSizes, setOrder, reset, isCustomised };
};

export default useColumnLayout;
